import React from 'react';
import PropTypes from 'prop-types';

import styled from '@emotion/styled';
import { css } from '@emotion/core';

const checkedStyles = (props) => css`
  color: ${(props.inverted ? props.theme.color.white : props.theme.color.brand)};

  ::after {
    opacity: 1
  }

  ::before {
    border-color: ${(props.inverted ? props.theme.color.white : props.theme.color.brand)};
  }
`;

const FILTERED_CHECKBOX_EMO_PROPS = new Set(['active', 'inverted', 'revamp']);

export const StyledCheckbox = styled('label', {
  shouldForwardProp: (prop) => !FILTERED_CHECKBOX_EMO_PROPS.has(prop)
})`
  color: ${({ inverted, revamp, theme }) => {
    if (revamp) return theme.color.bluePrimary;
    return inverted ? theme.color.white : theme.color.brand;
  }};
  cursor: pointer;
  display: block;
  font-family: ${(props) => props.theme.font.sans};
  font-size: ${({ theme, revamp }) => (revamp ? theme.modularScale.sixteen : theme.modularScale.small)};
  line-height: 18px;
  padding: 0.5rem 0 0.5rem ${(props) => (props.revamp ? '1.5em' : '2em')};
  position: relative;
  transition-duration: ${(props) => props.theme.animation.default};
  transition-property: color;
  transition-timing-function: ${(props) => props.theme.animation.easeOutQuad};

  ::after,
  ::before {
    position: absolute;
    transition-duration: ${(props) => props.theme.animation.default};
    transition-timing-function: ${(props) => props.theme.animation.easeOutQuad};
  }

  ::after {
    background: ${(props) => (props.inverted ? props.theme.color.white : props.theme.color.brand)};
    content: "";
    display: inline-block;
    height: 1px;
    left: ${(props) => (props.revamp ? '-2px' : '-1px')};
    opacity: 0;
    top: ${(props) => (props.revamp ? '13.47px' : '13px')};
    transform: rotate(45deg);
    transition-property: opacity;
    width: ${(props) => (props.revamp ? '1em' : 'calc(1em + 2px)')};
  }

  ::before {
    border-color: ${(props) => (props.inverted ? props.theme.color.white : props.theme.color.brand)};
    border-radius: 1px;
    border-style: solid;
    border-width: 1px;
    content: "";
    display: inline-block;
    height: ${(props) => (props.revamp ? '13px' : '1em')};
    left: 0;
    top: 8px;
    transition-property: border-color;
    width: ${(props) => (props.revamp ? '13px' : '1em')};
  }

  ${(props) => (props.active ? checkedStyles : '')};

  input[type="checkbox"] {
    opacity: 0;
    width: 0;
    position: absolute;
  }

  &.is-hidden {
    display: none;
  }
`;

class Checkbox extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      checked: this.props.active
    };

    this.handleOnChange = this.handleOnChange.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.active !== prevProps.active) {
      this.updateCheckedStatus(this.props.active);
    }
  }

  updateCheckedStatus(checked) {
    this.setState({ checked });
  }

  handleOnChange(e) {
    this.props.changed();
    this.setState({ checked: e.target.checked });
  }

  render() {
    return (
      <StyledCheckbox
        id={this.props.labelID}
        htmlFor={this.props.id}
        name={this.props.name}
        inverted={this.props.inverted}
        active={this.state.checked}
        className={this.props.className}
        revamp={this.props.revamp}
        data-test-id={this.props['data-test-id']}
      >
        {
          this.props.forwardedRef
            ? (
              <input
                ref={this.props.forwardedRef}
                id={this.props.id}
                type="checkbox"
                name={this.props.name}
                value={this.props.value}
                checked={this.state.checked}
                onChange={this.handleOnChange}
              />
            ) : (
              <input
                id={this.props.id}
                type="checkbox"
                name={this.props.name}
                value={this.props.value}
                checked={this.state.checked}
                onChange={this.handleOnChange}
              />
            )
        }
        {this.props.children}
      </StyledCheckbox>
    );
  }
}

Checkbox.defaultProps = {
  active: false,
  className: '',
  inverted: false,
  forwardedRef: () => { },
  changed: () => { },
  revamp: false,
  'data-test-id': 'CheckBox',
  labelID: ''
};

Checkbox.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
    PropTypes.object
  ]).isRequired,
  className: PropTypes.string,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  id: PropTypes.string.isRequired,
  inverted: PropTypes.bool,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  changed: PropTypes.func,
  revamp: PropTypes.bool,
  'data-test-id': PropTypes.string,
  labelID: PropTypes.string
};

export default Checkbox;
